import React, { useState, useEffect } from 'react';
import { Card, Upload, Table, Button, Switch, message, Select, Input, Space, Row, Col, Dropdown, Modal, Tooltip, notification } from 'antd';
import { ArrowLeftOutlined, CloseCircleOutlined, InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadFile, RcFile } from 'antd/es/upload/interface';
import { useDropdownValues } from '../hooks/usePaymentManager';
import { getColumns, getActionColumn} from '../helpers/columns';
import { PaymentData, FilterFormValues } from '../types/main';
import { handleUploadData, handleAllocate, pollOperationStatus, pollUploadStatus } from '../helpers/tableFunctions';
import { getStatusText, type NullableStatusResponse } from '../helpers/statusUtils';
import { formatRowData, handleDataFileUpload } from '../helpers/fileHandlers';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import AllocationManager from './AllocationManager/AllocationManager';
import UploadProgress from './UploadProgress'
import { authAxios } from '../../../utils/session_utils';
import { makeKeyInLowerCaseHelper } from '../../../utils';
import { ViewershipData } from '../../../utils/common.interface';
import AllocationManagerFilter from '../modals/AllocationManagerFilter';
const { Option } = Select;


const PaymentManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'upload' | 'allocation'>('allocation');
  const [fetchedPlatformsObj, setFetchedPlatformsObj] = useState<Record<string, boolean>>({});
  const [dataSource, setDataSource] = useState<PaymentData[]>([]);
  const [unappliedPaymentData, setUnAppliedPaymentData] = useState<PaymentData[]>([]);
  const [editingKey, setEditingKey] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [status, setStatus] = useState<NullableStatusResponse>(null);
  const [operationId, setOperationId] = useState<string | null>(null);
  const [isProgressModalVisible, setIsProgressModalVisible] = useState(false);
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [allocationProgress, setAllocationProgress] = useState<NullableStatusResponse>(null);
  const [viewershipPool, setViewershipPool] = useState<any>({});
  const [selectedRecord, setSelectedRecord] = useState<PaymentData | null>(null);
  const [allocationStatus, setAllocationStatus] = useState<string>('initial');
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [filterData, setFilterData] = useState<FilterFormValues | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({
    domain: true,
    platform: true,
    partner: true,
    amount: true,
    channel: true,
    territory: true,
    department: true,
    title: true,
    type: true,
    description: true,
    label: true,
    registerName: true,
    year: true,
    quarter: true,
    month: true,
    impressions: true,
    cpms: true,
    paymentDate: true,
});
  const dropdownValues = useDropdownValues();
  const toggleColumnVisibility = (columnKey: string) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const handleModeChange = (checked: boolean) => {
    setMode(checked ? 'allocation' : 'upload');
  };

  const isEditing = (record: PaymentData) => record.key === editingKey;

  const edit = (record: PaymentData) => {
    setEditingKey(record.key);
  };

  const save = (key: string) => {
    const row = dataSource.find(item => item.key === key);
    if (row) {
      setDataSource(prev => prev.map(item => 
        item.key === key ? row : item
      ));
    }
    setEditingKey('');
  };

  const handleInputChange = (value: string, key: string, column: keyof PaymentData) => {
    setDataSource(prev => prev.map(item => 
      item.key === key ? { ...item, [column]: value } : item
    ));
  };

  const onFileUpload = async (file: UploadFile | RcFile) => {
    const isSuccess = await handleDataFileUpload(file, setDataSource);
    if (isSuccess) {
      setMode('upload');
    }
  };


  useEffect(() => {
    if (mode === 'allocation') {
      getNonAppliedPaymentsData(filterData);
    }
  }, [])

  const getNonAppliedPaymentsData = async(filterData: FilterFormValues | null) => {
    try {
      setLoading(true);
      
      // Always fetch payments data only
      const config = {
        method: "GET",
        url: `${process.env.REACT_APP_BACK_END_API}/register-data/payments${filterData ? `?filter=${JSON.stringify(filterData)}` : ''}`
      }
      const {data} = await authAxios(config);
      const lowerCaseDataPayment = await makeKeyInLowerCaseHelper(data.paymentData);
      const formattedData = lowerCaseDataPayment.map((row: any, index: number) => formatRowData(row, index)); 
      setUnAppliedPaymentData(formattedData as PaymentData[]);
    } catch (error: any) {
      console.error("Error fetching payments data:", error?.response?.data ?? error);
    } finally {
      setLoading(false);
    }
  }

  const getViewershipData = async(filterData: FilterFormValues | null) => {
    try {
      if (!filterData?.platform) return message.info('Platform is required to fetch viewership data');
      const objKey = JSON.stringify(filterData);
      if (!filterData || fetchedPlatformsObj[objKey]) return;
      const config = {
        method: "GET",
        url: `${process.env.REACT_APP_BACK_END_API}/register-data/viewership${filterData ? `?filter=${JSON.stringify(filterData)}` : ''}`
      }
      const {data} = await authAxios(config);
      setViewershipPool({...viewershipPool, ...data.viewershipData as ViewershipData[]});
      setViewershipPool(data.viewershipData as ViewershipData[]);
      message.success('Viewership data fetched successfully!');
      // setFetchedPlatformsObj({...fetchedPlatformsObj, [objKey]: true});
    } catch (error: any) {
      message.error('Error fetching viewership data!');
      console.error("Error fetching viewership data:", error?.response?.data ?? error);
    }
  }

  const removeFilter = () => {
    setFilterData(null);
    setIsFilterModalVisible(false);
    getNonAppliedPaymentsData(null);
    // setViewershipPool([]);
  }


  const setCompletionState = () => {
    setEditingKey('');
    setIsUploading(false);
    setStatus(null);
    setOperationId(null);
    setIsProgressModalVisible(false);
    setIsErrorModalVisible(false);
    setErrorMessage('');
    setAllocationProgress(null);
    setSelectedRecord(null);
    setAllocationStatus('');
    setIsSuccessModalVisible(true);
    setFilterData(null);
    setUnAppliedPaymentData([]);
    setViewershipPool([]);
  };


  const renderCell = (value: string, record: PaymentData, column: keyof PaymentData) => {
    const editing = isEditing(record);
    if (editing) {
      if (column === 'domain') {
        return (
          <Select
            value={value}
            onChange={(val) => handleInputChange(val, record.key, column)}
            style={{ width: '100%' }}
          >
            {dropdownValues.domains.sort().map((domain) => (
              <Option key={domain} value={domain}>{domain}</Option>
            ))}
          </Select>
        );
      } else if (column === 'platform') {
        return (
          <Select
            value={value}
            onChange={(val) => handleInputChange(val, record.key, column)}
            style={{ width: '100%' }}
            disabled={!record.domain}
          >
            {(dropdownValues.platformsByDomain[record.domain] || []).sort().map((platform) => (
              <Option key={platform} value={platform}>{platform}</Option>
            ))}
          </Select>
        );
      } else if (column === 'partner') {
        return (
          <Select
            value={value}
            onChange={(val) => handleInputChange(val, record.key, column)}
            style={{ width: '100%' }}
            disabled={!record.platform}
          >
            {(dropdownValues.partnersByPlatform[record.platform] || []).sort().map((partner) => (
              <Option key={partner} value={partner}>{partner}</Option>
            ))}
          </Select>
        );
      } else if (column === 'label') {
        return (
          <Select
            value={value}
            onChange={(val) => handleInputChange(val, record.key, column)}
            style={{ width: '100%' }}
          >
            <Option value="Revenue">Revenue</Option>
            <Option value="Expense">Expense</Option>
          </Select>
        );
      }
      return (
        <Input
          value={value}
          onChange={(e) => handleInputChange(e.target.value, record.key, column)}
          style={{ width: '100%' }}
        />
      );
    }
    return value;
  };

  const columns = getColumns({
    visibleColumns,
    toggleColumnVisibility,
    renderCell
  });

  const actionColumn = getActionColumn({
    isEditing,
    edit,
    save,
    editingKey
  });


  const handleUploadSuccess = () => {
    setMode('allocation');
    getNonAppliedPaymentsData(null);
    setIsUploading(false);
    setDataSource([]);
    setIsProgressModalVisible(false);
  };



  const handleInitiateAllocate = async (keys: string[]) => {
    // Reset allocation progress explicitly
    setAllocationProgress({ status: 'allocation_started', message: 'Starting allocation' });
    setAllocationStatus('processing');
    try {
      await handleAllocate(
        keys,
        unappliedPaymentData, 
        setUnAppliedPaymentData,
        setAllocationStatus,
        setIsProgressModalVisible,
        setErrorMessage,
        setIsErrorModalVisible,
        setAllocationProgress,
        setIsSuccessModalVisible
      );
      return true;
    } catch (error) {
      setAllocationStatus('failure');
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred');
      setIsErrorModalVisible(true);
      return false;
    }
  };
  

  const handleFilterOk = (values: FilterFormValues) => {
    setFilterData(values);
    getNonAppliedPaymentsData(values);
    setIsFilterModalVisible(false);
  };


  return (
		<div className="p-4">
			<Card className="mb-4" bodyStyle={{ padding: "8px" }}>
				{/* Header with Mode Selection */}
				<Row className="w-full mb-6" align="middle" justify="space-between">
					<Col>
						<h1 className="text-2xl font-bold">Payment Management</h1>
					</Col>
					<Col>
            <Upload accept=".csv,.xlsx,.xls" beforeUpload={onFileUpload} showUploadList={false}>
              <Button icon={<UploadOutlined />}>Import Payment Data</Button>
            </Upload>
					</Col>
				</Row>

				{/* Action Buttons Row */}
				<Row className="w-full">
					<Col span={12}>
						{mode === "upload" ? (
							<Space size="middle">
								<Button icon={<ArrowLeftOutlined />} type="dashed" danger onClick={() => setMode('allocation')}>Switch to Allocation Mode</Button>

								<Dropdown
									menu={{
										items: [
											{
												key: "show-hide-all",
												label: (
													<div className="px-2 pb-2 border-b border-gray-200">
														<Button
															type="text"
															onClick={() => {
																const allHidden = Object.values(visibleColumns).every(v => v);
																setVisibleColumns(prev =>
																	Object.keys(prev).reduce(
																		(acc, key) => ({
																			...acc,
																			[key]: !allHidden,
																		}),
																		{}
																	)
																);
															}}
														>
															{Object.values(visibleColumns).every(v => v)
																? "Hide All"
																: "Show All"}
														</Button>
													</div>
												),
											},
											...Object.entries(visibleColumns).map(([key, value]) => ({
												key,
												label: (
													<div className="flex items-center gap-2 px-2 py-1">
														<Switch
															size="small"
															checked={value}
															onChange={() => toggleColumnVisibility(key)}
														/>
														<span>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
													</div>
												),
											})),
										],
									}}
									trigger={["click"]}
								>
									<Button>Column Settings</Button>
								</Dropdown>
							</Space>
						) : (
							<Space size="middle">
                 <Tooltip
											title={
												<>
													{filterData ? (
														<span className="text-xs text-gray-500">
															(Platform: {filterData.platform}. Month:{" "}
															{filterData.month?.join(", ")}, Year: {filterData.year?.join(", ")})
														</span>
													) : (
														"Select Filters"
													)}
												</>
											}
										>
								<Button
									type="primary"
									onClick={() => setIsFilterModalVisible(true)}
									icon={
											<InfoCircleOutlined />
									}
								>
										{filterData ? "Update Filters" : "Select Filters"}
								</Button>
                </Tooltip>
								{filterData && (
                  <CloseCircleOutlined style={{color: 'red', fontSize: '20px'}} onClick={() => removeFilter()} />
									
								)}
							</Space>
						)}
					</Col>

					<Col span={12} style={{ textAlign: "right" }}>
						{mode === "upload" && (
							<Button
								onClick={() =>
									handleUploadData(
										dataSource,
										setIsUploading,
										setStatus,
										setOperationId,
										setViewershipPool,
										setIsProgressModalVisible,
										setErrorMessage,
										setIsErrorModalVisible,
										handleUploadSuccess
									)
								}
								disabled={dataSource.length === 0 || isUploading}
								loading={isUploading}
								type="primary"
								className="bg-blue-600 hover:bg-blue-500"
							>
								Upload to Database
							</Button>
						)}
					</Col>
				</Row>

				{/* Content Section */}
				{mode === "upload" ? (
					/* Upload View */
					<Table
						dataSource={dataSource}
						columns={[...columns, actionColumn]}
						pagination={false}
						scroll={{ x: "max-content" }}
					/>
				) : (
					/* Allocation View */
					<AllocationManager
            getViewershipData={getViewershipData}
						unappliedPaymentData={unappliedPaymentData}
						setUnAppliedPaymentData={setUnAppliedPaymentData}
						handleAllocate={handleInitiateAllocate}
						selectedRecord={selectedRecord}
						setSelectedRecord={setSelectedRecord}
						viewershipPool={viewershipPool}
						allocationStatus={allocationStatus}
					/>
				)}
			</Card>

			{/* Modals */}
			<Modal
				title={mode === "upload" ? "Upload Progress" : "Allocation Progress"}
				open={isProgressModalVisible}
				footer={null}
				closable={false}
			>
				<div className="text-center">
					<UploadProgress uploadStatus={mode === "upload" ? status : allocationProgress} />
					<p className="mt-4">{getStatusText(mode === "upload" ? status : allocationProgress)}</p>
				</div>
			</Modal>
			<Modal
				title={
					<div className="flex items-center text-red-600">
						<ExclamationCircleOutlined className="mr-2 text-xl" /> {" "}
						<span className="font-bold">Error</span>
					</div>
				}
				open={isErrorModalVisible}
				onCancel={() => setIsErrorModalVisible(false)}
				footer={[
					<Button key="close" type="primary" danger onClick={() => setIsErrorModalVisible(false)}>
						Close
					</Button>,
				]}
				centered
			>
				<div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
					<p className="text-red-700 font-bold text-lg mb-2">An Error Occurred</p>
					<p className="text-red-600">{errorMessage}</p>
				</div>
			</Modal>
			<Modal
				title="Job Successfully Launched"
				open={isSuccessModalVisible}
				onOk={() => setIsSuccessModalVisible(false)}
				onCancel={() => setIsSuccessModalVisible(false)}
				footer={[
					<Button
						key="ok"
						type="primary"
						onClick={() => setIsSuccessModalVisible(false)}
						className="bg-green-600 hover:bg-green-500"
					>
						OK
					</Button>,
				]}
				centered
			>
				<div className="text-center p-4">
					<div className="mb-4 text-green-600 text-xl">✓</div>
					<p className="text-lg font-medium mb-2">The job is now being processed.</p>
					<p className="text-gray-600">
						You will receive an email when it is complete. (2-3 minutes)
					</p>
				</div>
			</Modal>
			<AllocationManagerFilter
				visible={isFilterModalVisible}
				onCancel={() => setIsFilterModalVisible(false)}
				onOk={handleFilterOk}
				loading={loading}
			/>
		</div>
	);
}

export default PaymentManagement;
